import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useLazyQuery } from '@apollo/client';

import { Spinner } from 'admin-components/components';
import { Column } from 'components/shared/Layout';
import { GET_LUGGAGE_PRODUCTS } from 'components/shared/AddAssistance/queries';
import { transformLuggageToExtras } from 'components/shared/AddAssistance/AddAssistanceForm/utils';
import { reducer, getInitialState } from 'components/shared/AddAssistance/AddAssistanceForm/AddAssistanceForm.reducer';
import {
  onToggleAssistanceVisibilityAction,
  onRemoveAllItemsAction,
  onRemoveItemAction,
  onAddItemAction,
  onEditItemAction
} from 'components/shared/AddAssistance/AddAssistanceForm/AddAssistanceForm.actions';
import AddAssistanceOptions from 'components/shared/AddAssistance/AddAssistanceForm/AddAssistanceOptions';
import { StyledGenericMessage, StyledMessage } from 'components/shared/AddAssistance/AddAssistance.styles';
import Button from 'components/ui/Button';
import Modal from 'components/ui/Modal';
import Select from 'components/ui/Select';
import { ConfirmButton } from 'components/ui/Button';
import { StyledAbandonButton } from 'components/shared/OnHoldCancellationModal/OnHoldCancellationModal.styles';
import { PassengerSection, PassengerHeader, PassengerName, JourneyHeading } from './HeterogeneousLuggageExtrasSection.styles';

const buildJourneyOption = (id) => ({
  id,
  translationId: `addAssistance.journey.${id}`
});

const HeterogeneousLuggageExtrasSection = ({ currency, booking, passengers, onStateChange, intl }) => {
  const [passengerLuggageData, setPassengerLuggageData] = useState([]);
  const [currentSearchPassengerId, setCurrentSearchPassengerId] = useState(null);
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [selectedPassengerId, setSelectedPassengerId] = useState('');

  const outbound = useMemo(() => booking?.eurostarOutbound || [], [booking?.eurostarOutbound]);
  const inbound = useMemo(() => booking?.eurostarInbound || [], [booking?.eurostarInbound]);
  const allBookingItems = useMemo(() => booking?.allBookingItems || [], [booking?.allBookingItems]);
  const uncanceledOutbound = useMemo(() => outbound.filter(({ cancelled }) => !cancelled) ?? [], [outbound]);
  const uncanceledInbound = useMemo(() => inbound.filter(({ cancelled }) => !cancelled) ?? [], [inbound]);

  const travelingPassengers = new Set();
  allBookingItems.forEach(({ passengerId, cancelled, canBeRebooked }) => {
    if (!cancelled && canBeRebooked) {
      travelingPassengers.add(passengerId);
    }
  });
  const activePassengers = passengers.filter(({ id }) => travelingPassengers.has(id));

  const alreadyAddedPassengerIds = passengerLuggageData.map((p) => p.passengerId);

  const handleAddExtraClick = () => {
    const availablePassengers = activePassengers.filter((p) => !alreadyAddedPassengerIds.includes(p.id));

    if (availablePassengers.length === 0) {
      return;
    }

    if (availablePassengers.length === 1) {
      setCurrentSearchPassengerId(availablePassengers[0].id);
    } else {
      setShowPassengerModal(true);
    }
  };

  const handlePassengerConfirm = () => {
    setShowPassengerModal(false);
    setCurrentSearchPassengerId(selectedPassengerId);
  };

  const handleModalClose = () => {
    setShowPassengerModal(false);
    setSelectedPassengerId('');
  };

  const handleRemovePassenger = (passengerId) => {
    setPassengerLuggageData((prev) => prev.filter((p) => p.passengerId !== passengerId));
  };

  const handlePassengerStateChange = (passengerId, newState) => {
    setPassengerLuggageData((prev) => prev.map((p) => (p.passengerId === passengerId ? { ...p, state: newState } : p)));
  };

  const [getLuggageProducts, { loading: luggageProductsLoading }] = useLazyQuery(GET_LUGGAGE_PRODUCTS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data && currentSearchPassengerId) {
        const passenger = passengers.find((p) => p.id === currentSearchPassengerId);
        const transformedExtras = transformLuggageToExtras(data, outbound, inbound);
        const extrasOptions = [...(transformedExtras.outbound || []), ...(transformedExtras.inbound || [])];

        setPassengerLuggageData((prev) => [
          ...prev,
          {
            passengerId: currentSearchPassengerId,
            passengerName: `${passenger.firstName} ${passenger.lastName}`,
            transformedExtras,
            state: getInitialState([], [], extrasOptions, [passenger], transformedExtras.inbound?.length > 0, currentSearchPassengerId)
          }
        ]);

        setCurrentSearchPassengerId(null);
      }
    }
  });

  useEffect(() => {
    if (currentSearchPassengerId) {
      const passenger = passengers.find((p) => p.id === currentSearchPassengerId);
      const passengerItems = allBookingItems.filter(
        (item) => item.passengerId === currentSearchPassengerId && !item.cancelled && item.canBeRebooked
      );

      if (passengerItems.length === 0) {
        setCurrentSearchPassengerId(null);
        return;
      }

      const passengerItemRefs = passengerItems.map((i) => i.itemRef);
      const passengerOutbound = uncanceledOutbound.filter((leg) => leg.bookingItems?.some((bi) => passengerItemRefs.includes(bi.itemRef)));
      const passengerInbound = uncanceledInbound.filter((leg) => leg.bookingItems?.some((bi) => passengerItemRefs.includes(bi.itemRef)));

      getLuggageProducts({
        variables: {
          currency,
          exchangeItems: passengerItems.map((item) => Number(item.itemRef)),
          reference: booking?.reference || '',
          passengers: [
            {
              id: passenger.id,
              type: passenger.type,
              disabilityType: passenger.disabilityType
            }
          ],
          origin: passengerOutbound.length > 0 ? passengerOutbound[0]?.origin?.uic : passengerInbound[0]?.origin?.uic,
          destination:
            passengerOutbound.length > 0
              ? passengerOutbound[passengerOutbound.length - 1]?.destination?.uic
              : passengerInbound[passengerInbound.length - 1]?.destination?.uic,
          ...(passengerOutbound.length > 0 && {
            outboundDate: passengerOutbound[0]?.timing?.date
          }),
          ...(passengerInbound.length > 0 && {
            inboundDate: passengerInbound[0]?.timing?.date
          }),
          trainNumbers: Array.from(new Set([...passengerOutbound.map((o) => o.trainNumber), ...passengerInbound.map((o) => o.trainNumber)]))
        }
      });
    }
  }, [currentSearchPassengerId, getLuggageProducts, passengers, allBookingItems, uncanceledOutbound, uncanceledInbound, booking, currency]);

  useEffect(() => {
    if (onStateChange) {
      if (passengerLuggageData.length === 0) {
        onStateChange({
          assistance: {},
          isAssistanceIncomplete: false,
          formPayload: { add: [], update: [], remove: [], removedDetails: [] },
          transformedLuggageExtras: { outbound: [], inbound: [] }
        });
      } else {
        const aggregatedState = passengerLuggageData.reduce(
          (acc, { state: paxState, transformedExtras }) => ({
            assistance: { ...acc.assistance, ...paxState.assistance },
            isAssistanceIncomplete: acc.isAssistanceIncomplete || paxState.isAssistanceIncomplete,
            formPayload: {
              add: [...(acc.formPayload?.add || []), ...(paxState.formPayload?.add || [])],
              update: [...(acc.formPayload?.update || []), ...(paxState.formPayload?.update || [])],
              remove: [...(acc.formPayload?.remove || []), ...(paxState.formPayload?.remove || [])],
              removedDetails: [...(acc.formPayload?.removedDetails || []), ...(paxState.formPayload?.removedDetails || [])]
            },
            transformedLuggageExtras: {
              outbound: [...(acc.transformedLuggageExtras?.outbound || []), ...(transformedExtras?.outbound || [])],
              inbound: [...(acc.transformedLuggageExtras?.inbound || []), ...(transformedExtras?.inbound || [])]
            }
          }),
          {
            assistance: {},
            isAssistanceIncomplete: false,
            formPayload: { add: [], update: [], remove: [], removedDetails: [] },
            transformedLuggageExtras: { outbound: [], inbound: [] }
          }
        );
        onStateChange(aggregatedState);
      }
    }
  }, [passengerLuggageData, onStateChange]);

  return (
    <>
      <Modal isOpen={showPassengerModal} title={<FormattedMessage id="addAssistance.selectPassenger" />} onClose={handleModalClose}>
        <Select
          value={selectedPassengerId}
          name="passengerId"
          data-testid="passengerId"
          onChange={(event) => setSelectedPassengerId(event.target.value)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'addAssistance.selectPassenger' })}
          </option>
          {passengers.map(({ id, firstName, lastName }) => {
            const isAlreadyAdded = alreadyAddedPassengerIds.includes(id);
            const hasNoTickets = !activePassengers.find((p) => p.id === id);

            return (
              <option key={id} value={id} disabled={isAlreadyAdded || hasNoTickets}>
                {`${firstName} ${lastName}`}
              </option>
            );
          })}
        </Select>

        <ConfirmButton data-testid="searchExtras" onClick={handlePassengerConfirm} disabled={!selectedPassengerId}>
          <FormattedMessage id="searchBooking.search" />
        </ConfirmButton>

        <StyledAbandonButton data-testid="cancelPassengerSelection" onClick={handleModalClose}>
          <FormattedMessage id="extras.cancel" />
        </StyledAbandonButton>
      </Modal>

      {luggageProductsLoading && (
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      )}

      {passengerLuggageData.map(({ passengerId, passengerName, transformedExtras, state }) => {
        const dispatch = (action) => {
          const newState = reducer(state, action);
          handlePassengerStateChange(passengerId, newState);
        };

        const hasOutbound = transformedExtras.outbound?.length > 0;
        const hasInbound = transformedExtras.inbound?.length > 0;
        const passengerList = passengers.filter((p) => p.id === passengerId);

        return (
          <PassengerSection key={passengerId}>
            <PassengerHeader>
              <PassengerName>{passengerName}</PassengerName>
              <Button type="button" onClick={() => handleRemovePassenger(passengerId)} data-cy={`remove-passenger-${passengerId}`}>
                <FormattedMessage id="extrasOnlyBookings.removeExtra" />
              </Button>
            </PassengerHeader>

            <JourneyHeading>
              <FormattedMessage id="addAssistance.journey.outbound" />
            </JourneyHeading>
            {hasOutbound ? (
              transformedExtras.outbound.map((extra) => {
                const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[extra.id] || {
                  isAddPassengersDisabled: false,
                  isVisible: false,
                  items: [],
                  isIncomplete: false
                };

                return (
                  <AddAssistanceOptions
                    currency={currency}
                    key={extra.id}
                    isIncomplete={isIncomplete}
                    assistance={extra}
                    isExtra
                    directions={[buildJourneyOption(extra.direction)]}
                    passengers={passengerList}
                    isAddPassengersDisabled={isAddPassengersDisabled}
                    isItemsVisible={isVisible}
                    items={items}
                    onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(extra.id))}
                    onAddItem={() => dispatch(onAddItemAction(extra.id))}
                    onChangeItem={(item) => dispatch(onEditItemAction({ key: extra.id, item }))}
                    onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(extra.id))}
                    onRemoveItem={(item) => dispatch(onRemoveItemAction({ key: extra.id, item }))}
                  />
                );
              })
            ) : (
              <StyledMessage>
                <FormattedMessage id="addExtras.noAvailability" />
              </StyledMessage>
            )}
            {uncanceledInbound?.length > 0 && (
              <>
                <JourneyHeading>
                  <FormattedMessage id="addAssistance.journey.inbound" />
                </JourneyHeading>
                {hasInbound ? (
                  transformedExtras.inbound.map((extra) => {
                    const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[extra.id] || {
                      isAddPassengersDisabled: false,
                      isVisible: false,
                      items: [],
                      isIncomplete: false
                    };

                    return (
                      <AddAssistanceOptions
                        currency={currency}
                        key={extra.id}
                        isIncomplete={isIncomplete}
                        assistance={extra}
                        isExtra
                        directions={[buildJourneyOption(extra.direction)]}
                        passengers={passengerList}
                        isAddPassengersDisabled={isAddPassengersDisabled}
                        isItemsVisible={isVisible}
                        items={items}
                        onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(extra.id))}
                        onAddItem={() => dispatch(onAddItemAction(extra.id))}
                        onChangeItem={(item) => dispatch(onEditItemAction({ key: extra.id, item }))}
                        onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(extra.id))}
                        onRemoveItem={(item) => dispatch(onRemoveItemAction({ key: extra.id, item }))}
                      />
                    );
                  })
                ) : (
                  <StyledMessage>
                    <FormattedMessage id="addExtras.noAvailability" />
                  </StyledMessage>
                )}
              </>
            )}
          </PassengerSection>
        );
      })}

      {passengerLuggageData.some((p) => p.state.isAssistanceIncomplete) && booking && (
        <StyledGenericMessage data-cy="luggage-extras-warning">
          <FormattedMessage id="addAssistance.generalWarning" />
        </StyledGenericMessage>
      )}

      {!luggageProductsLoading && (
        <Button type="button" onClick={handleAddExtraClick} data-cy="add-luggage-extras-button" disabled={travelingPassengers.size === 0}>
          <FormattedMessage id="extrasOnlyBookings.addNewExtra" />
        </Button>
      )}
    </>
  );
};

HeterogeneousLuggageExtrasSection.propTypes = {
  currency: PropTypes.string.isRequired,
  booking: PropTypes.object,
  passengers: PropTypes.array.isRequired,
  onStateChange: PropTypes.func,
  intl: PropTypes.object.isRequired
};

export default injectIntl(HeterogeneousLuggageExtrasSection);
